import React, { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTenant } from "../context/TenantContext";
import {
  FaBars,
  FaChevronDown,
  FaChevronRight,
  FaChalkboardTeacher,
  FaClipboardList,
  FaTimes,
  FaUserCircle,
  FaUserCog,
  FaVideo,
  FaChartArea,
  FaCalendarCheck,
  FaBook,
  FaChartBar,
} from "react-icons/fa";

const teacherSubmenuItems = [
  { key: "overview", label: "Overview", icon: FaChartArea },
  { key: "online-classes", label: "Online Class", icon: FaVideo },
  { key: "attendance", label: "Attendance", icon: FaCalendarCheck },
  { key: "students", label: "Students", icon: FaUserCircle },
  { key: "resources", label: "Resources", icon: FaBook },
  { key: "insights", label: "Insights", icon: FaChartBar },
  { key: "assessments", label: "Assessments", icon: FaClipboardList },
  { key: "usage", label: "Usage", icon: FaChartBar },
  { key: "account", label: "Account", icon: FaUserCog },
  { key: "my-profile", label: "My Profile", icon: FaUserCog, to: "/profile" },
];

const TeacherSidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState("overview");

  const { activeTenant, isSubdomainTenant, tenantLogoUrl, schoolName: contextSchoolName } = useTenant();
  const isSubdomain = Boolean(isSubdomainTenant || (activeTenant?.subdomain && activeTenant?.brandingEnabled === true));
  const showSchoolBranding = activeTenant?.brandingEnabled !== false && activeTenant?.whiteLabelEnabled === true;
  const schoolName = contextSchoolName || activeTenant?.schoolName || activeTenant?.name || "";
  const schoolLogo = tenantLogoUrl || activeTenant?.logoUrl || activeTenant?.schoolLogoUrl;

  const displayName = useMemo(() => {
    const rawUserName = localStorage.getItem("currentUserName") || localStorage.getItem("username") || "";
    if (!rawUserName) return "Teacher";

    return rawUserName
      .split("@")[0]
      .replace(/[._-]+/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  }, []);

  const handleSubmenuClick = (item) => {
    setActiveMenu(item.key);

    if (item.to) {
      navigate(item.to);
      setIsMobileOpen(false);
      return;
    }

    const dispatchAction = () => {
      window.dispatchEvent(new CustomEvent("teacher-dashboard:select-action", {
        detail: { action: item.key },
      }));
    };

    if (location.pathname !== "/teacher-dashboard") {
      navigate("/teacher-dashboard");
      window.setTimeout(dispatchAction, 120);
    } else {
      dispatchAction();
    }

    setIsMobileOpen(false);
  };

  const SidebarInner = ({ compact = false }) => (
    <div className="flex h-full min-h-0 flex-col bg-[#17202b]">
      <div className="flex shrink-0 items-center gap-3 border-b border-white/5 px-5 py-5">
        <div className="theme-sidebar-avatar flex h-11 w-11 items-center justify-center overflow-hidden rounded-xl">
          {showSchoolBranding && schoolLogo ? (
            <img src={schoolLogo} alt={`${schoolName} logo`} className="h-full w-full object-contain p-1" />
          ) : (
            <FaUserCircle className="text-lg" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-bold leading-none text-white">
            {showSchoolBranding ? (schoolName || "Teacher workspace") : <>Class<span className="text-sky-400">NX</span></>}
          </p>
          <p className="mt-1 truncate text-[9px] font-bold uppercase tracking-[0.16em] text-slate-500">Teacher workspace</p>
        </div>
        {compact && (
          <button
            type="button"
            aria-label="Close sidebar"
            onClick={() => setIsMobileOpen(false)}
            className="theme-sidebar-close ml-auto inline-flex rounded-lg p-2 transition"
          >
            <FaTimes />
          </button>
        )}
      </div>

      <nav className="min-h-0 flex-1 overflow-y-auto px-3 py-4">
        <p className="mb-2 px-3 text-[9px] font-bold uppercase tracking-[0.2em] text-slate-500">Main navigation</p>
        <div className="space-y-1">
          {teacherSubmenuItems.map((item) => {
            const Icon = item.icon || FaChevronRight;
            const isActive = activeMenu === item.key || location.pathname === item.to;
            return (
              <button
                key={item.key}
                type="button"
                onClick={() => handleSubmenuClick(item)}
                className={`group flex w-full items-center gap-3 rounded-md border-l-2 px-3 py-2.5 text-left text-[13px] font-medium transition ${
                  isActive
                  ? "border-cyan-400 bg-white/[0.08] text-white"
                    : "border-transparent text-slate-400 hover:bg-white/[0.04] hover:text-white"
                }`}
              >
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-white/[0.05] text-xs text-sky-400"><Icon /></span>
                <span className="min-w-0 flex-1 truncate">{item.label}</span><FaChevronRight className="text-[8px] text-slate-600" />
              </button>
            );
          })}
        </div>
      </nav>
    </div>
  );

  return (
    <>
      <button
        type="button"
        onClick={() => setIsMobileOpen(true)}
        className="theme-menu-fab fixed bottom-6 right-6 z-40 inline-flex h-14 w-14 items-center justify-center rounded-full border shadow-lg lg:hidden"
        title="Open menu"
      >
        <FaBars className="text-xl" />
      </button>

      {isMobileOpen && (
        <button
          type="button"
          aria-label="Close menu overlay"
          onClick={() => setIsMobileOpen(false)}
          className="theme-menu-overlay fixed inset-0 z-30 lg:hidden"
        />
      )}

      <aside
        className="fixed bottom-[4.25rem] left-0 top-[4.5rem] z-40 w-[82vw] max-w-[17rem] overflow-hidden border-r border-slate-800 bg-[#17202b] text-slate-100 shadow-2xl lg:hidden"
        style={{ transform: isMobileOpen ? "translateX(0)" : "translateX(-100%)", transition: "transform 300ms ease" }}
      >
        <div className="h-full overflow-y-auto pr-1 smooth-scrollbar">
          <SidebarInner compact />
        </div>
      </aside>

      <aside className="fixed bottom-0 left-0 top-0 z-[60] hidden w-[248px] flex-col overflow-hidden border-r border-slate-800 bg-[#17202b] text-slate-100 shadow-xl lg:flex">
        <div className="min-h-0 flex-1 overflow-y-auto pr-1 smooth-scrollbar">
          <SidebarInner />
        </div>
      </aside>
    </>
  );
};

export default TeacherSidebar;
